import { useState } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { backupDatabase } from '../../services/importData';
import { BackupRestoreModal } from '../BackupRestoreModal';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

interface DatabaseBackupProps {
  className?: string;
}

export const DatabaseBackup = ({ className }: DatabaseBackupProps) => {
  const [loading, setLoading] = useState(false);
  const [selectedTables, setSelectedTables] = useState<string[]>(['users', 'properties', 'monthly_stats']);
  const [backupFormat, setBackupFormat] = useState<'excel' | 'json'>('excel');
  const [includeTimestamp, setIncludeTimestamp] = useState(true);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const availableTables = [
    { id: 'users', name: 'Usuarios' },
    { id: 'properties', name: 'Propiedades' },
    { id: 'monthly_stats', name: 'Datos Mensuales' }
  ];

  const handleToggleTable = (tableId: string) => {
    if (selectedTables.includes(tableId)) {
      setSelectedTables(selectedTables.filter(id => id !== tableId));
    } else {
      setSelectedTables([...selectedTables, tableId]);
    }
  };

  const handleSelectAllTables = () => {
    setSelectedTables(availableTables.map(table => table.id));
  };

  const handleDeselectAllTables = () => {
    setSelectedTables([]);
  };

  const handleCreateBackup = async () => {
    if (selectedTables.length === 0) {
      toast.error('Selecciona al menos una tabla para hacer la copia de seguridad');
      return;
    }

    setLoading(true);
    try {
      // Obtener los datos de todas las tablas
      const allBackupData = await backupDatabase();
      
      // Filtrar solo las tablas seleccionadas
      const filteredBackupData: Record<string, any[]> = {};
      for (const table of selectedTables) {
        if (allBackupData[table]) {
          filteredBackupData[table] = allBackupData[table];
        }
      }
      
      // Generar el nombre del archivo
      const timestamp = includeTimestamp ? `_${new Date().toISOString().replace(/[:.]/g, '-')}` : '';
      const fileName = `inmohost_backup${timestamp}`;
      
      if (backupFormat === 'excel') {
        // Crear un libro de trabajo con múltiples hojas (una por tabla)
        const wb = XLSX.utils.book_new();
        
        // Añadir cada tabla como una hoja
        for (const [tableName, tableData] of Object.entries(filteredBackupData)) {
          const ws = XLSX.utils.json_to_sheet(tableData);
          XLSX.utils.book_append_sheet(wb, ws, tableName);
        }
        
        // Descargar el archivo
        XLSX.writeFile(wb, `${fileName}.xlsx`);
      } else {
        // Formato JSON
        const jsonData = JSON.stringify(filteredBackupData, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        // Crear un enlace para descargar el archivo
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      toast.success('Copia de seguridad creada correctamente');
    } catch (error: any) {
      console.error('Error al crear copia de seguridad:', error);
      toast.error(error.message || 'Error al crear copia de seguridad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={className}>
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">Copia de Seguridad de la Base de Datos</h2>
        
        <div className="space-y-4">
          {/* Selección de tablas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecciona las tablas a incluir:
            </label>
            <div className="grid grid-cols-2 gap-2">
              {availableTables.map(table => (
                <div key={table.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`table-${table.id}`}
                    checked={selectedTables.includes(table.id)}
                    onChange={() => handleToggleTable(table.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`table-${table.id}`} className="ml-2 text-sm text-gray-700">
                    {table.name}
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-2 flex space-x-2">
              <button
                type="button"
                onClick={handleSelectAllTables}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Seleccionar todo
              </button>
              <button
                type="button"
                onClick={handleDeselectAllTables}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Deseleccionar todo
              </button>
            </div>
          </div>
          
          {/* Formato de backup */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Formato de la copia de seguridad:
            </label>
            <div className="flex space-x-4">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="format-excel"
                  name="backup-format"
                  value="excel"
                  checked={backupFormat === 'excel'}
                  onChange={() => setBackupFormat('excel')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="format-excel" className="ml-2 text-sm text-gray-700">
                  Excel (.xlsx)
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="format-json"
                  name="backup-format"
                  value="json"
                  checked={backupFormat === 'json'}
                  onChange={() => setBackupFormat('json')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="format-json" className="ml-2 text-sm text-gray-700">
                  JSON (.json)
                </label>
              </div>
            </div>
          </div>
          
          {/* Opciones adicionales */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opciones adicionales:
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="include-timestamp"
                checked={includeTimestamp}
                onChange={() => setIncludeTimestamp(!includeTimestamp)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="include-timestamp" className="ml-2 text-sm text-gray-700">
                Incluir fecha y hora en el nombre del archivo
              </label>
            </div>
          </div>
          
          {/* Botones de backup y restauración */}
          <div className="pt-2 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleCreateBackup}
                loading={loading}
                disabled={selectedTables.length === 0}
                variant="primary"
                className="flex-1"
              >
                📥 Crear Copia de Seguridad
              </Button>
              <Button
                onClick={() => setShowRestoreModal(true)}
                variant="secondary"
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
              >
                🔄 Restaurar Backup
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              💾 La copia de seguridad se descargará automáticamente. Guárdala en un lugar seguro.
            </p>
          </div>
        </div>
      </div>

      {/* Modal de restauración */}
      <BackupRestoreModal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        onSuccess={() => {
          toast.success('Datos restaurados correctamente. Recargando página...');
          setTimeout(() => window.location.reload(), 2000);
        }}
      />
    </Card>
  );
};
